import gsap from 'gsap'
import {Color, Group, Mesh, PlaneGeometry, ShaderMaterial, Vector3, Vector4} from 'three'

import type {EnterpriseHeroRegistration} from '../../components/WebGLContext/WebGLContext.types'
import {getRenderer} from '../_commonObjects'
import {canvasSize, windowSize} from '../_config'
import {PADDING} from '../_rawConfig'
import fragmentShader from '../_shaders/enterpriseHero/bg.ts'
import vertexShader from '../_shaders/vertexShader.ts'
import {CORNER_RADIUS} from '../modules/cube/_config'
import type {CubeConfig} from '../modules/cube/_types'
import InvertocatCube from '../modules/cube/InvertocatCube'
import RoundedCubeGeometry from '../modules/cube/RoundedCubeGeometry'
import SingleCube from '../modules/cube/SingleCube'
import cubeLayout from './cubeLayout'

export default class EnterpriseHero {
  bgGroup: Group = new Group()
  group: Group = new Group()
  cubeGroup: Group = new Group()
  id: string | null = null
  element: HTMLElement
  _cubeGeometry: RoundedCubeGeometry
  singleCubes: SingleCube[] = []
  rectData: Vector4 = new Vector4(1, 1, 1, 1)
  _rectData: Vector4 = new Vector4(1, 1, 1, 1)
  tl: gsap.core.Timeline = gsap.timeline({paused: true})
  groupScale: number = 12
  scrollProgress: number = 0
  isAnimationStarted: boolean = false
  invertocatCube: InvertocatCube | null = null
  defaultRot: Vector3 = new Vector3(-Math.PI * 0.2, -Math.PI * 0.25, 0)
  animRot: Vector3 = new Vector3(-Math.PI * 0.2, 0, 0)
  constructor() {
    this._cubeGeometry = new RoundedCubeGeometry(CORNER_RADIUS, 8)
    this.cubeGroup.rotation.order = 'XYZ'

    this.cubeGroup.userData._position = new Vector3(0, -4, 0)

    this.tl
      .to(
        this.animRot,
        {
          x: this.defaultRot.x,
          y: this.defaultRot.y,
          z: this.defaultRot.z,
          duration: 2,
          ease: 'power2.out',
        },
        0,
      )

      .to(
        this.cubeGroup.userData._position,
        {
          y: 0,
          ease: 'power2.out',
          duration: 2,
        },
        0,
      )

    const dammyElement = document.createElement('div')
    this.element = dammyElement

    this.group.add(this.cubeGroup)
    this.group.add(this.bgGroup)
    // const axesHelper = new AxesHelper(5);
    // this.cubeGroup.add(axesHelper);
  }
  updateRegisterEnterpriseHero(enterpriseHero: EnterpriseHeroRegistration) {
    this.id = enterpriseHero.id
    this.element = enterpriseHero.element

    this.group.userData.rectData = this.rectData // width, height, left, top
    this.group.userData._rectData = this._rectData // width, height, left, top

    this.createScene()
  }

  unregisterEnterpriseHero() {
    this.group.clear()
    this.cubeGroup.clear()
    this.bgGroup.clear()
    this.id = null
  }

  async createScene() {
    cubeLayout.forEach((cubeConfig: CubeConfig, index: number) => {
      if (cubeConfig.cubeType === 'invertocat') {
        const invertocatParams = {
          position: new Vector3(0.67, 0, 0),
          rotation: new Vector3(-Math.PI * 0.5, Math.PI * 0.5, 0),
        }
        this.invertocatCube = new InvertocatCube(
          {
            cubeConfig,
            cubeGeometry: this._cubeGeometry,
            rectData: this.rectData,
            _rectData: this._rectData,
          },
          invertocatParams,
        )

        this.invertocatCube.create(this.cubeGroup)
        this.invertocatCube.lightPos.set(1, -1, 1).normalize()
      } else {
        const singleCube = new SingleCube({
          cubeConfig,
          cubeGeometry: this._cubeGeometry,
          rectData: this.rectData,
          _rectData: this._rectData,
        })

        const cubeMesh = singleCube.mesh
        this.cubeGroup.add(cubeMesh)
        cubeMesh.renderOrder = index

        singleCube.createAnimation(this.tl)

        if (cubeConfig.cubeType === 'glassNoBlur') {
          singleCube.progress.w = 4
          this.tl.to(
            singleCube.progress,
            {
              w: 0,
              duration: 3,
            },
            cubeConfig.delay + 3,
          )
        }

        if (cubeConfig.cubeType === 'glassRainbowNoBlur') {
          singleCube.progress.w = 2
          this.tl.to(
            singleCube.progress,
            {
              w: 0,
              duration: 1,
            },
            cubeConfig.delay + 0.5,
          )
        }

        this.singleCubes.push(singleCube)
      }
    })

    const progress = new Vector4(0, 0, 0, 0)

    const bgPlane = new Mesh(
      new PlaneGeometry(1, 1),
      new ShaderMaterial({
        uniforms: {
          uColor1: {value: new Color(0xa0defb)},
          uColor2: {value: new Color(0xe7e5fb)},
          uColor3: {value: new Color(0xeaf3f4)},
          uRectData: {value: this.rectData},
          uProgress: {value: progress},
        },
        vertexShader,
        fragmentShader,
      }),
    )

    this.tl
      .to(
        progress,
        {
          x: 1,
          duration: 2,
        },
        0,
      )
      .to(
        progress,
        {
          y: 1,
          duration: 4,
        },
        0,
      )

    this.bgGroup.add(bgPlane)
    this.bgGroup.userData.progress = progress
  }

  resize() {
    this.updateRectData()
  }

  updateRectData() {
    const rect = this.element.getBoundingClientRect()

    this._rectData.set(rect.width, rect.height, rect.left, canvasSize.y - rect.top - windowSize.y * PADDING)

    this.rectData.copy(this._rectData)
    const renderer = getRenderer()
    if (renderer) {
      this.rectData.multiplyScalar(renderer.getPixelRatio())
    }

    return rect
  }

  update(frameDelta: number) {
    const rect = this.updateRectData()
    // y -= rect.height / 4

    const x = rect.left + rect.width / 2 - windowSize.x / 2
    const y = -(rect.top + rect.height / 2 - windowSize.y / 2)

    const scale = Math.min(1600, rect.width) / this.groupScale
    this.cubeGroup.scale.set(scale, scale, scale)
    this.group.position.set(x, y, 0)

    this.bgGroup.scale.set(rect.width, rect.height, 1)
    this.bgGroup.position.z = -1000

    this.cubeGroup.position.y = this.cubeGroup.userData._position.y * scale
    this.cubeGroup.position.y += this.scrollProgress * scale * 0.6

    this.cubeGroup.rotation.set(this.animRot.x, this.animRot.y, this.animRot.z)
    this.cubeGroup.rotation.x -= this.scrollProgress * Math.PI * 0.1
    // this.cubeGroup.rotation.y += this.scrollProgress * Math.PI * 0.07;

    // Check if it's in viewport to render
    const isInViewport = rect.bottom >= 0 && rect.right >= 0 && rect.top <= windowSize.y && rect.left <= windowSize.x

    this.scrollProgress = window.scrollY / (windowSize.y + rect.height)
    this.scrollProgress = Math.min(Math.max(this.scrollProgress, 0), 1)
    // this.scrollProgress = this.scrollProgress - 0.5;

    if (this.invertocatCube) {
      if (this.invertocatCube.isLoaded && !this.isAnimationStarted && isInViewport) {
        this.isAnimationStarted = true
        this.tl.play()
        this.invertocatCube.playTl()
      }
    }

    if (isInViewport) {
      const renderer = getRenderer()
      if (!renderer) return
      this.singleCubes.forEach(singleCube => {
        singleCube.update(renderer)
      })

      if (this.invertocatCube) {
        this.invertocatCube.update()
      }
    }
  }
}
